// Slice `journey` da Jornada de Configuração. Segue o shape padrão da casa
// ({ data, loading, lastUpdate }); `data` guarda o payload do contrato
// GET /config-journey (workspace + modules[]).
//
// Slice `guide` controla o drawer fixo (guia da tarefa) que persiste sobre a
// tela de destino real. `open` liga o drawer; `moduleId`/`taskId` identificam a
// tarefa exibida (resolvida contra `journey.data.modules` pelo getter guideContext).
// O estado é persistido em localStorage (chave `cc_guide_<escritorio>_<usuario>`,
// ver utils/guide-storage.js) e restaurado por `restoreGuide` no created do
// layout `main.vue` — não no boot, porque a chave depende do usuário logado.
import { GUIDE_ORIGIN_JOURNEY } from '@modules/central-config/utils/guide-origin'

export default {
  journey: {
    loading: false,
    lastUpdate: null,
    data: {
      // Contrato: workspace é um objeto { office, professional } (não string).
      workspace: { office: '', professional: '' },
      generatedAt: '',
      modules: [],
    },
  },
  guide: {
    open: false,
    // `collapsed` recolhe o painel para um FAB (bolinha) no canto inferior
    // direito, junto ao dock de atalhos — o guia continua ativo (open), só some
    // o painel lateral. Expandir volta a mostrar o painel.
    collapsed: false,
    moduleId: null,
    taskId: null,
    // De onde o guia foi aberto — decide o destino do botão de voltar do header
    // ('journey' volta à home da Jornada, 'module' volta ao início do módulo).
    // Descreve a SESSÃO do guia, não a etapa: sobrevive a goPrev/goNext e ao
    // recolher para o FAB. Ver utils/guide-origin.js e utils/module-routes.js.
    // ⚠️ Ao contrário de returnToModuleId (abaixo), estes DOIS campos são
    // persistidos em localStorage — sem isso o F5 perde a origem e o botão passa
    // a levar ao lugar errado, sem nenhum erro visível.
    origin: GUIDE_ORIGIN_JOURNEY,
    // `slug` do módulo no catálogo (não o UUID) — é a chave de MODULE_KEY_TO_ROUTE.
    moduleKey: null,
  },
  // Transição "Iniciar etapa" (2026-07-25): navigatingTaskId tinge a linha no
  // accordion; exitingModuleId dispara a saída da lista do módulo. Setados por
  // startTask() no accordion (via utils/step-navigation.js) e resetados no
  // router.afterEach global — ou diretamente por startTaskNavigation() quando
  // não há navegação real (sem task.url) ou o router.push é rejeitado, casos em
  // que o afterEach nunca dispara (ver utils/step-navigation.js).
  stepTransition: {
    navigatingTaskId: null,
    exitingModuleId: null,
  },
  // Nível do escritório na Jornada (spec-004). Espelha `value.nivel_atual` do
  // GET /config-journey — NUNCA é calculado no front: quem decide promoção
  // (auto-avanço ao completar as obrigatórias do nível) é o backend, porque a
  // promoção é uma ESCRITA de estado. Desde 2026-07-29 a progressão é 100%
  // automática — não há troca manual, o front só exibe.
  // ⚠️ `progresso_por_nivel` do payload é DESCARTADO desde 2026-08-05: com o
  // filtro de contratação, o número do backend conta etapas que a tela não
  // mostra. Quem calcula é `progressoPorNivel` (utils/progress.js), sobre os
  // módulos visíveis. Só o `nivel` continua do backend — é estado persistido.
  nivelAtual: {
    nivel: 1,
  },
  // Módulo de onde o usuário saiu para executar uma etapa. Ao voltar à Jornada
  // (`backToJourney`), a home usa isto para reabrir no mesmo ponto em vez de
  // jogar o usuário no topo — `router.push` não é navegação de HISTÓRICO, então
  // o `scrollBehavior` do router não tem `savedPosition` para restaurar.
  // Consumido UMA vez e limpo (não é preferência persistida).
  returnToModuleId: null,
  // Tour guiado (jornada guiada, 2026-08-14): o overlay que escurece a tela,
  // abre o furo sobre o elemento real do passo e leva o cursor até lá. Os
  // TEXTOS vêm de `metadata.instructions[]` do catálogo; os SELETORES vêm do
  // módulo (fee-fixo/constants/tour-fee-fixo.js) — este slice guarda o
  // resultado já casado, e por isso o central-config nunca importa fee-fixo.
  //
  // ⚠️ Ao contrário do `guide` logo acima, este slice NÃO é persistido em
  // localStorage — e isso é decisão, não esquecimento. O drawer é material de
  // consulta: restaurá-lo devolve algo que a pessoa estava lendo. O tour é uma
  // coreografia apontando para um elemento do DOM: voltar no passo 3 depois de
  // um F5 significa modal fechado, cenário perdido e o furo sobre um elemento
  // num estado que o texto do passo não descreve — o tour volta mentindo. F5
  // encerra, e a etapa continua pendente no card (o status é do backend).
  // `central-config-tour-guiado.unit.js` trava a ausência de persistência.
  tour: {
    ativo: false,
    // uuid do módulo (para marcarTask) e slug (chave do mapa de seletores):
    // o id é opaco por regra do módulo, o slug é o que se lê em review.
    moduleId: null,
    moduleKey: null,
    taskId: null,
    taskSlug: null,
    // Passos já casados: [{ titulo, detalhe, alvo, avancaNoClique, aguarda }].
    passos: [],
    indice: 0,
    // 'passos' | 'desfecho'. Separado de `indice === passos.length` de
    // propósito: o desfecho não é um passo de instructions[] (tem botões
    // próprios e nenhum alvo), e codificá-lo como off-by-one quebra em
    // silêncio quando o backend acrescenta uma instrução.
    fase: 'passos',
    // O tour recolheu o drawer da Jornada ao arrancar? Só reexpande no fim se
    // foi ele quem recolheu.
    recolheuGuia: false,
    // Checkbox do desfecho, ligado por padrão: marcar segue sendo declaração
    // do usuário, mas quem só percorreu não paga um clique a mais.
    marcarConcluida: true,
    // { taskId, slug, rotulo, url } da próxima etapa guiável, ou null. Resolvida
    // por QUEM INICIA o tour (que tem o mapa de seletores do módulo) — a casca
    // no central-config só a exibe, para não precisar conhecer o módulo.
    proximaEtapa: null,
  },
  // Etapa de onde o usuário saiu, no par com `returnToModuleId`. É ELA que
  // define o nível a reabrir: um módulo cross-nível aparece em mais de uma
  // seção, e só o módulo não diz de qual delas o usuário desceu (o helper de
  // módulo escolhe a menor, que pode nem conter a etapa trabalhada). Limpa
  // junto com o módulo — os dois são um estado só, de uso único.
  returnToTaskId: null,
}
